import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addToProject,
  adminProductReindex,
  createProject,
  getAdminProduct,
  getAdminProductAvailabilityOpts,
  getAdminProductColorOpts,
  getAdminProductConstructionOpts,
  getAdminProductPatternOpts,
  getAdminProductVisibilityOpts,
  getProduct,
  getProducts,
  getProjectSearch,
  removeFromProject, sendCuttingRequest,
  saveAdminProduct, getCuttingRequest,
} from '@/app/products/api/endpoints.ts';
import { useNotifications } from '@/hooks/use-notifications.ts';
import { router } from '@/router/router.tsx';

export function useProductsQuery(params: any) {
  return useQuery({
    queryFn: () => getProducts(params),
    queryKey: ['products', params],
    placeholderData: (prev) => prev,
  });
}

export function useAdminProductQuery(id: number, colorId?: number) {
  return useQuery({
    queryFn: () => getAdminProduct(id, colorId),
    queryKey: ['admin-product', id, colorId],
  });
}

export function useAdminProductSaveMutation() {
  const {mutateProps, showSuccess} = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => saveAdminProduct(data),
    ...mutateProps,
    onSuccess: async (res, req) => {
      if (res.success) {
        await queryClient.invalidateQueries({ queryKey: ['products'] });
        await queryClient.invalidateQueries({ queryKey: ['admin-product', req.id] });
        showSuccess('Product saved successfully');
      }
    },
  });
}

export function useAdminProductReindexMutation() {
  const {mutateProps, showSuccess} = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminProductReindex(id),
    ...mutateProps,
    onSuccess: async (res, id) => {
      if (res.success) {
        await queryClient.invalidateQueries({ queryKey: ['products'] });
        await queryClient.invalidateQueries({ queryKey: ['admin-product', id] });
        showSuccess('Product re-indexed successfully');
      }
    },
  });
}

export function useAdminProductVisibilityOptsQuery() {
  return useQuery({
    queryFn: () => getAdminProductVisibilityOpts(),
    queryKey: ['admin-product-visibility-opts'],
  });
}

export function useAdminProductAvailabilityOptsQuery() {
  return useQuery({
    queryFn: () => getAdminProductAvailabilityOpts(),
    queryKey: ['admin-product-availability-opts'],
  });
}

export function useAdminProductConstructionOptsQuery() {
  return useQuery({
    queryFn: () => getAdminProductConstructionOpts(),
    queryKey: ['admin-product-construction-opts'],
  });
}

export function useAdminProductPatternOptsQuery() {
  return useQuery({
    queryFn: () => getAdminProductPatternOpts(),
    queryKey: ['admin-product-pattern-opts'],
  });
}

export function useAdminProductColorOptsQuery() {
  return useQuery({
    queryFn: () => getAdminProductColorOpts(),
    queryKey: ['admin-product-color-opts'],
  });
}

export function useProjectSearchQuery(params: any) {
  return useQuery({
    queryFn: () => getProjectSearch(params),
    queryKey: ['project-search', params],
  });
}

async function clearProjectQueries(queryClient: QueryClient, productId?: any) {
  await queryClient.invalidateQueries({ queryKey: ['products'] });
  await queryClient.invalidateQueries({ queryKey: ['project-search'] });
  if (productId) {
    await queryClient.invalidateQueries({ queryKey: ['product', String(productId)], refetchType: 'all' });
  }
  await router.invalidate();
}

export function useProjectCreateMutation() {
  const {mutateProps, showSuccess} = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => createProject(data),
    ...mutateProps,
    onSuccess: async (res, req) => {
      if (res.success) {
        await clearProjectQueries(queryClient, req.productId)
        showSuccess('Project created successfully');
      }
    },
  });
}

export function useAddToProjectMutation() {
  const {mutateProps, showSuccess} = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => addToProject(data),
    ...mutateProps,
    onSuccess: async (res, req) => {
      if (res.success) {
        await clearProjectQueries(queryClient, req.productId);
        showSuccess('Added to project successfully');
      }
    },
  });
}

export function useRemoveFromProjectMutation() {
  const {mutateProps, showSuccess} = useNotifications();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => removeFromProject(data),
    ...mutateProps,
    onSuccess: async (res, req) => {
      if (res.success) {
        await clearProjectQueries(queryClient, req.productId);
        showSuccess('Removed product from project successfully');
      }
    },
  });
}

export function getProductQuery(productId: number, colorId?: number) {
  return {
    queryFn: () => getProduct(productId, colorId),
    queryKey: ['product', String(productId), String(colorId)],
  };
}

export function useGetRequestCuttingQuery(colorId: number) {
  return useQuery({
    queryFn: () => getCuttingRequest(colorId) as any,
    queryKey: ['request-cutting', colorId],
  });
}

export function useCuttingRequestMutation() {
  const {mutateProps} = useNotifications();
  return useMutation({
    mutationFn: (colorId: number) => sendCuttingRequest(colorId),
    ...mutateProps,
  });
}